import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { Box } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";

import ToastComponent from "../../../../../components/Toast";
import { useMetadatas } from "../../../../../hooks/metadata";
import { language } from "../../../../../language";
import FormBuilder from "../../../../smartForms";

const StyledTypography = styled(Typography)(({ theme }) => ({
  flexGrow: 1,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const INITIAL_FORM_STATE = {
  id: "",
  name: "",
  description: "",
  category: "",
  draft: true,
};

const FormModeler = ({
  returnschema,
  FormComponents,
  activityName,
  allFormFields,
  businessObject,
  saveForm,
  element,
  extensionElements,
  selectBox,
  bpmnFactory,
  saveFormOnApi,
  fetchCreateForm,
  fetchEditForm,
  saved,
  savedError,
  edited,
  editedError,
  fetchEditFormClear,
  fetchCreateFormClear,
  published,
  publishedError,
  fetchPublishFormClear,
  fetchPublishForm,
}) => {
  const [openFormModeler, setOpenFormModeler] = useState(true);
  const [metadatasIsReady, setMetadatasIsReady] = useState(false);
  const [formDataState, setFormDataState] = useState(INITIAL_FORM_STATE);
  const [formHasSaved, setFormHasSaved] = useState(false);
  const [savedForm, setSavedForm] = useState(false);

  const { data: metadatas } = useMetadatas({ params: { isPageable: false } });

  useEffect(() => {
    returnschema(FormComponents && { components: FormComponents });
  }, [returnschema, FormComponents]);

  useEffect(() => {
    if (metadatas) {
      setMetadatasIsReady(true);
    }
  }, [metadatas]);

  const handleClose = () => {
    setOpenFormModeler(false);
    setFormHasSaved(false);
    setSavedForm(false);
  };

  const handlePublish = () => {
    fetchPublishForm(savedForm);
  };

  const handleSaveOnDiagram = () => {
    saveForm(
      businessObject,
      element,
      extensionElements,
      selectBox,
      bpmnFactory,
      savedForm,
    );
    setOpenFormModeler(false);
  };

  const handleSaveOnApi = (event, formValues) => {
    const formToSave = formValues;

    //se o form ja foi salvo uma vez, então adiciona a versão no state
    if (formHasSaved) {
      formToSave.version = savedForm.version;
    }

    setFormDataState(formToSave);
    saveFormOnApi(
      businessObject,
      element,
      extensionElements,
      selectBox,
      bpmnFactory,
      fetchCreateForm,
      fetchEditForm,
      formToSave,
      formHasSaved,
    );
  };

  const handleOnChange = (schema) => {
    returnschema(schema && { components: schema.lines });
  };

  const handleChangeFieldValue = (prop) => (event) => {
    setFormDataState({
      ...formDataState,
      [prop]: event.target.value,
    });
  };

  const form = { display: "form", components: FormComponents };

  return (
    <Dialog fullScreen open={openFormModeler} onClose={handleClose}>
      <Box sx={{ width: "100%", height: "100%" }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="secondary"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <StyledTypography variant="h6">
              {`SmartForm - ${activityName}`}
            </StyledTypography>

            <StyledButton
              color="inherit"
              onClick={handleSaveOnDiagram}
              startIcon={<SaveIcon />}
            >
              {language.save}
            </StyledButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ height: "calc(100% - 64px)" }}>
          {metadatasIsReady && (
            <FormBuilder
              metadatas={metadatas}
              form={form}
              onChange={handleOnChange}
              canCreateMetadata
              allFormFields={allFormFields}
              businessObject={businessObject}
            />
          )}
        </Box>
      </Box>

      {(savedError || editedError) && (
        <ToastComponent
          onClose={() => fetchCreateFormClear()}
          open={savedError || editedError}
          content={savedError ? savedError.message : editedError.message}
          overrideDuration={2000}
        />
      )}

      {(saved || savedError) && (
        <ToastComponent
          onClose={() => {
            saved && setFormHasSaved(true);
            saved && setSavedForm(saved);
            fetchCreateFormClear();
          }}
          open={saved || savedError}
          content={savedError ? savedError.message : language.savedForm}
          success={saved}
          overrideDuration={1500}
        />
      )}

      {(edited || editedError) && (
        <ToastComponent
          onClose={() => {
            edited && setFormHasSaved(true);
            edited && setSavedForm(edited);
            fetchEditFormClear();
          }}
          open={edited || editedError}
          content={editedError ? editedError.message : "Rascunho salvo"}
          success={edited}
          overrideDuration={1500}
        />
      )}

      {(published || publishedError) && (
        <ToastComponent
          onClose={() => {
            publishedError && fetchPublishFormClear();
            published && handleSaveOnDiagram();
          }}
          open={published || publishedError}
          content={
            publishedError ? publishedError.message : "Formulário publicado"
          }
          success={published}
          overrideDuration={2000}
        />
      )}
    </Dialog>
  );
};

export default FormModeler;
